/**
 * ImageSelectQuiz Student Renderer
 * Same rendering as the adult version since image-select works cleanly for both modes.
 * Uses useImageSelectQuiz headless hook for logic.
 */

import { cn } from '../../../lib/utils'
import { SubmitButton, QuizSolveDescription, QuizHeader, QuizResultToggle, type QuizResultToggleValue } from '../shared'
import { formatText } from '../../../utils/quiz-parser'
import type { QuizSolveResult } from '../../../types/legacyQuiz'
import { useImageSelectQuiz } from '../hooks'
import type { ImageSelectQuizData } from '../hooks'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'

interface ImageSelectStudentProps {
  quizId: string | number
  quiz: ImageSelectQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
  /** true면 결과 토글은 유지하되 풀이/해설 UI만 숨긴다. */
  hideSolveDescription?: boolean
  /** G4 — true면 유형별 노출표에 따라 발문/내용만 흐리고 선택 이미지는 그대로 둔다. */
  dimChoiceText?: boolean
}

export function ImageSelectStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false,
}: ImageSelectStudentProps) {
  const { t } = useTranslation('quiz')

  const {
    selectedKeys,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleToggle,
    handleSubmit,
  } = useImageSelectQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  return (
    <div className="space-y-6">
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.imageSelect.label')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        variant="student"
        image={undefined}
        imageMobile={undefined}
        desmos={quiz.desmos}
        vars={quiz.vars}
        dimTitleText={dimChoiceText}
        dimProblemText={dimChoiceText}
        problem={quiz.problem && (
          <div className="text-xl leading-relaxed text-gray-900">
            {formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
          </div>
        )}
      />

      {/* Image + overlay rectangles */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex items-center justify-center"
      >
        <div className="relative inline-block">
          {/* Base image */}
          {quiz.image && (
            <img
              src={quiz.image.signedUrl || quiz.image.url}
              alt="quiz"
              className="max-w-full h-auto"
            />
          )}

          {/* Overlay rectangles */}
          {quiz.choices.map((rect) => {
            const isSelected = selectedKeys.has(rect.key)
            const isCorrectAnswer = solveResult?.correctAnswers?.includes(rect.key)

            // Clamp values
            const x = Math.max(0, rect.x)
            const y = Math.max(0, rect.y)
            const width = rect.x + rect.width > 100 ? 100 - rect.x : rect.width
            const height = rect.y + rect.height > 100 ? 100 - rect.y : rect.height

            const showAsCorrect = isSolved && shouldShowCorrectAnswers && isCorrectAnswer
            const showAsMyCorrect = isSolved && !shouldShowCorrectAnswers && isSelected && isCorrectAnswer
            const showAsWrong = isSolved && !shouldShowCorrectAnswers && isSelected && !isCorrectAnswer

            return (
              <button
                key={rect.key}
                onClick={() => handleToggle(rect.key)}
                disabled={isSolved || isViewSolved}
                className={cn(
                  'group absolute cursor-pointer border-4 border-transparent rounded-xl transition-all',
                  !isSolved && !isViewSolved && !isSelected && 'hover:border-white hover:shadow-lg',
                  !isSolved && isSelected && 'border-white shadow-lg',
                  (isSolved || isViewSolved) && 'pointer-events-none'
                )}
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  width: `${width}%`,
                  height: `${height}%`,
                }}
              >
                {/* Inner overlay */}
                <div className={cn(
                  'absolute inset-0 rounded-lg border-2 transition-all',
                  !isSolved && !isSelected && 'border-dashed border-lc-lightblue-900 opacity-0 group-hover:opacity-100',
                  !isSolved && isSelected && 'border-solid border-lc-lightblue-900 bg-lc-lightblue-900/20 opacity-100',
                  (showAsCorrect || showAsMyCorrect) && 'border-solid border-lc-mint-900 bg-lc-mint-900/20 opacity-100',
                  showAsWrong && 'border-solid border-lc-red-900 bg-lc-red-900/20 opacity-100',
                  shouldShowCorrectAnswers && isCorrectAnswer && 'border-solid border-lc-mint-900 bg-lc-mint-900/20 opacity-100'
                )} />
              </button>
            )
          })}
        </div>
      </motion.div>

      {/* Submit button */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel ?? (selectedKeys.size === 0
            ? t('types.imageSelect.selectArea')
            : t('common.checkAnswer'))}
        />
      )}

      {/* 풀이 결과 토글 (정답과 풀이 / 내가 쓴 답) */}
      {isSolved && !hideExplanation && (
        <QuizResultToggle
          value={isShowingCorrectAnswers ? 'correct-and-explanation' : 'my-answer'}
          onChange={(next: QuizResultToggleValue) => setIsShowingCorrectAnswers(next === 'correct-and-explanation')}
        />
      )}

      {/* 해설은 '정답과 풀이' 일 때만 노출 */}
      {isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={false}
        />
      )}
    </div>
  )
}
